import { useTranslation } from "react-i18next";
import React, { useState, useRef } from 'react';
import { Button, FormGroup, FormFeedback, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Loading from 'Components/Loading';
export default function PhotoUpload({
  onSubmit,
  currentValue,
  propertyId
}) {
  const {
    t
  } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cropData, setCropData] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // Handle file selection

  const handleFileSelect = event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setError('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError(null);
    processImage(file);
  }; // Process image file


  const processImage = file => {
    const reader = new FileReader();

    reader.onload = e => {
      setPreview(e.target.result);
      setShowCrop(true);
    };

    reader.readAsDataURL(file);
  }; // Start camera


  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 640
          },
          height: {
            ideal: 480
          }
        }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setShowCamera(true);
    } catch (err) {
      setError('Camera access denied or not available');
    }
  }; // Capture photo from camera


  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d'); // Set canvas size to video size

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight; // Draw video frame to canvas

    context.drawImage(video, 0, 0, canvas.width, canvas.height); // Convert to data URL

    const dataURL = canvas.toDataURL('image/jpeg', 0.8);
    setPreview(dataURL);
    setShowCrop(true);
    stopCamera();
  }; // Stop camera


  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setShowCamera(false);
  }; // Handle crop


  const handleCrop = croppedDataURL => {
    setCropData(croppedDataURL);
    setShowCrop(false);
  }; // Submit photo


  const submitPhoto = async () => {
    if (!cropData) return;
    setLoading(true);
    setError(null);

    try {
      // Convert data URL to blob for upload
      const response = await fetch(cropData);
      const blob = await response.blob(); // Create FormData for file upload

      const formData = new FormData();
      formData.append('image', blob, 'photo.jpg'); // Upload to server

      const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
      let uploadUrl = `${API_BASE}/users/profile/thumbnail`;

      if (propertyId) {
        uploadUrl += `?propertyId=${encodeURIComponent(propertyId)}`;
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('HMI_AUTH_TOKEN')}`
        },
        body: formData
      });
      const result = await uploadResponse.json();

      if (result.success) {
        // Use the base64 data URL from server
        await onSubmit(result.data.imageUrl);
        setPreview(null);
        setCropData(null);
      } else {
        throw new Error(result.error?.message || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload photo: ' + err.message);
    } finally {
      setLoading(false);
    }
  }; // Cancel crop


  const cancelCrop = () => {
    setShowCrop(false);
    setPreview(null);
    setCropData(null);
  };

  return <FormGroup>
      {loading && <Loading />}

      {
      /* Current photo display */
    }
      {currentValue && typeof currentValue === 'string' && currentValue.trim() && <div className="mb-3">
          <img src={currentValue.startsWith('data:') || currentValue.startsWith('http') || currentValue.startsWith('/') ? currentValue : `data:image/jpeg;base64,${currentValue}`} alt={t("Current")} style={{
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid #ddd'
      }} />
        </div>}

      {
      /* Upload buttons */
    }
      <div className="d-flex gap-2 mb-3">
        <Button color="primary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={loading}>
          <span role="img" aria-label="file folder">📁</span>{t("Choose File")}</Button>

        <Button color="secondary" size="sm" onClick={startCamera} disabled={loading}>
          <span role="img" aria-label="camera">📷</span>{t("Take Photo")}</Button>

        {
        /* Show crop button if there's an existing image */
      }
        {currentValue && typeof currentValue === 'string' && currentValue.trim() && <Button color="info" size="sm" onClick={() => {
        setPreview(currentValue.startsWith('data:') || currentValue.startsWith('http') || currentValue.startsWith('/') ? currentValue : `data:image/jpeg;base64,${currentValue}`);
        setShowCrop(true);
      }} disabled={loading}>
            <span role="img" aria-label="scissors">✂️</span>{t("Crop Current Photo")}</Button>}
      </div>

      {
      /* Hidden file input */
    }
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{
      display: 'none'
    }} />

      {
      /* Camera Modal */
    }
      <Modal isOpen={showCamera} toggle={stopCamera} size="lg">
        <ModalHeader toggle={stopCamera}>{t("Take Photo")}</ModalHeader>
        <ModalBody>
          <video ref={videoRef} autoPlay playsInline style={{
          width: '100%',
          maxWidth: '500px'
        }} />
          <canvas ref={canvasRef} style={{
          display: 'none'
        }} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={capturePhoto}>
            <span role="img" aria-label="camera">📷</span>{t("Capture")}</Button>
          <Button color="secondary" onClick={stopCamera}>{t("Cancel")}</Button>
        </ModalFooter>
      </Modal>

      {
      /* Crop Modal */
    }
      <Modal isOpen={showCrop} toggle={cancelCrop} size="lg">
        <ModalHeader toggle={cancelCrop}>{t("Crop Photo")}</ModalHeader>
        <ModalBody>
          <ImageCropper src={preview} onCrop={handleCrop} onCancel={cancelCrop} />
        </ModalBody>
      </Modal>

      {
      /* Submit button */
    }
      {cropData && <div className="mt-3">
          <img src={cropData} alt={t("Cropped preview")} style={{
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid #007bff'
      }} className="mb-2" />
          <div>
            <Button color="success" size="sm" onClick={submitPhoto} disabled={loading}>
              <span role="img" aria-label="check mark">✅</span>{t("Save Photo")}</Button>
            <Button color="secondary" size="sm" onClick={cancelCrop} className="ms-2">{t("Cancel")}</Button>
          </div>
        </div>}

      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>;
} // Simple image cropper component

function ImageCropper({
  src,
  onCrop,
  onCancel
}) {
  const {
    t
  } = useTranslation();
  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
    width: 200,
    height: 200
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [resetKey] = useState(0);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const handleMouseDown = e => {
    setIsDragging(true);
  };

  const handleResizeStart = (e, handle) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
  };

  const handleMouseMove = e => {
    if (!imageLoaded) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isDragging && !isResizing) {
      // Move crop area
      const newCrop = {
        x: Math.max(0, Math.min(x - crop.width / 2, rect.width - crop.width)),
        y: Math.max(0, Math.min(y - crop.height / 2, rect.height - crop.height)),
        width: crop.width,
        height: crop.height
      };
      setCrop(newCrop);
    } else if (isResizing && resizeHandle) {
      // Resize crop area
      let newCrop = { ...crop
      };

      switch (resizeHandle) {
        case 'nw':
          newCrop.width = Math.max(50, crop.width + (crop.x - x));
          newCrop.height = Math.max(50, crop.height + (crop.y - y));
          newCrop.x = Math.max(0, x);
          newCrop.y = Math.max(0, y);
          break;

        case 'ne':
          newCrop.width = Math.max(50, x - crop.x);
          newCrop.height = Math.max(50, crop.height + (crop.y - y));
          newCrop.y = Math.max(0, y);
          break;

        case 'sw':
          newCrop.width = Math.max(50, crop.width + (crop.x - x));
          newCrop.height = Math.max(50, y - crop.y);
          newCrop.x = Math.max(0, x);
          break;

        case 'se':
          newCrop.width = Math.max(50, x - crop.x);
          newCrop.height = Math.max(50, y - crop.y);
          break;

        default:
          // No action needed for unknown handles
          break;
      } // Ensure crop area stays within bounds


      newCrop.x = Math.max(0, Math.min(newCrop.x, rect.width - newCrop.width));
      newCrop.y = Math.max(0, Math.min(newCrop.y, rect.height - newCrop.height));
      newCrop.width = Math.min(newCrop.width, rect.width - newCrop.x);
      newCrop.height = Math.min(newCrop.height, rect.height - newCrop.y);
      setCrop(newCrop);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const resetToOriginal = () => {
    // Cancel the crop and return to original image
    onCancel();
  };

  const handleCrop = () => {
    if (!imageRef.current || !imageLoaded) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d'); // Use 300x300 for final output to match server processing

    canvas.width = 300;
    canvas.height = 300; // Calculate the actual crop area relative to the image

    const img = imageRef.current; // Calculate scale factor between displayed image and actual image

    const scaleX = img.naturalWidth / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight; // Convert crop coordinates to actual image coordinates

    const actualCrop = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY
    };
    ctx.drawImage(img, actualCrop.x, actualCrop.y, actualCrop.width, actualCrop.height, 0, 0, 300, 300);
    const croppedDataURL = canvas.toDataURL('image/jpeg', 0.9);
    onCrop(croppedDataURL);
  };

  return <div>
      <div ref={containerRef} style={{
      position: 'relative',
      display: 'inline-block',
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden'
    }} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
        <img ref={imageRef} src={src} alt={t("Crop preview")} style={{
        maxWidth: '500px',
        maxHeight: '400px',
        display: 'block'
      }} onLoad={() => {
        // Center the crop area when image loads
        if (imageRef.current && containerRef.current) {
          const img = imageRef.current;
          const containerRect = containerRef.current.getBoundingClientRect(); // Calculate crop size based on image size (max 200px)

          const maxCropSize = Math.min(200, Math.min(img.clientWidth, img.clientHeight) * 0.8);
          setCrop({
            x: (containerRect.width - maxCropSize) / 2,
            y: (containerRect.height - maxCropSize) / 2,
            width: maxCropSize,
            height: maxCropSize
          });
          setImageLoaded(true);
        }
      }} />

        {
        /* Dark overlay with crop window - Top */
      }
        <div key={`overlay-top-${resetKey}`} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: crop.y,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        transition: 'all 0.2s ease-in-out'
      }} />

        {
        /* Dark overlay with crop window - Bottom */
      }
        <div key={`overlay-bottom-${resetKey}`} style={{
        position: 'absolute',
        top: crop.y + crop.height,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        transition: 'all 0.2s ease-in-out'
      }} />

        {
        /* Dark overlay with crop window - Left */
      }
        <div key={`overlay-left-${resetKey}`} style={{
        position: 'absolute',
        top: crop.y,
        left: 0,
        width: crop.x,
        height: crop.height,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        transition: 'all 0.2s ease-in-out'
      }} />

        {
        /* Dark overlay with crop window - Right */
      }
        <div key={`overlay-right-${resetKey}`} style={{
        position: 'absolute',
        top: crop.y,
        left: crop.x + crop.width,
        right: 0,
        height: crop.height,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        transition: 'all 0.2s ease-in-out'
      }} />

        {
        /* Crop overlay with handles */
      }
        <div key={`crop-overlay-${resetKey}`} style={{
        position: 'absolute',
        left: crop.x,
        top: crop.y,
        width: crop.width,
        height: crop.height,
        border: '3px solid #007bff',
        cursor: 'move',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease-in-out'
      }} onMouseDown={handleMouseDown}>
          {
          /* Corner handles */
        }
          <div style={{
          position: 'absolute',
          top: -6,
          left: -6,
          width: 12,
          height: 12,
          backgroundColor: '#007bff',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'nw-resize'
        }} onMouseDown={e => handleResizeStart(e, 'nw')} />
          <div style={{
          position: 'absolute',
          top: -6,
          right: -6,
          width: 12,
          height: 12,
          backgroundColor: '#007bff',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'ne-resize'
        }} onMouseDown={e => handleResizeStart(e, 'ne')} />
          <div style={{
          position: 'absolute',
          bottom: -6,
          left: -6,
          width: 12,
          height: 12,
          backgroundColor: '#007bff',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'sw-resize'
        }} onMouseDown={e => handleResizeStart(e, 'sw')} />
          <div style={{
          position: 'absolute',
          bottom: -6,
          right: -6,
          width: 12,
          height: 12,
          backgroundColor: '#007bff',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'se-resize'
        }} onMouseDown={e => handleResizeStart(e, 'se')} />
        </div>
      </div>

      <div className="mt-3">
        <Button color="primary" onClick={handleCrop}>
          <span role="img" aria-label="scissors">✂️</span>{t("Crop & Save")}</Button>
        <Button color="warning" onClick={resetToOriginal} className="ms-2">
          <span role="img" aria-label="counterclockwise arrows">🔄</span>{t("Back to Original")}</Button>
        <Button color="secondary" onClick={onCancel} className="ms-2">{t("Cancel")}</Button>
      </div>
    </div>;
}